import { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { format, subDays, parseISO, isBefore } from 'date-fns';
import { toast } from 'sonner';
import { haptics } from '@/hooks/useHaptics';
import { calculateLevel } from '@/lib/gamification';
import { ensureSession } from '@/lib/ensureSession';
import { queryKeys } from '@/lib/queryKeys';
import { emitPushPrompt, hasPushPromptFired } from '@/lib/pushPromptBus';
import { triggerScoreRecompute } from '@/lib/health/triggerScoreRecompute';

export interface UserProfile {
  id: string;
  display_name: string | null;
  calories_goal: number;
  protein_goal: number;
  carbs_goal: number;
  fats_goal: number;
  water_goal: number;
  current_streak: number;
  longest_streak: number;
  total_xp: number;
  level: number;
  last_active_date: string | null;
  created_at: string;
  // Physical stats for macro calculation
  weight_kg: number | null;
  height_cm: number | null;
  age: number | null;
  gender: string | null;
  activity_level: string | null;
  fitness_goal: 'lose_weight' | 'build_muscle' | 'maintain' | 'hyrox' | string | null;
  hyrox_division: string | null;
  hyrox_category: string | null;
  hyrox_race_date: string | null;
  hyrox_race_name: string | null;
  hyrox_weak_station: string | null;
  equipment: 'commercial_gym' | 'home_gym' | 'minimal' | 'bodyweight' | string | null;
  injuries: string | null;
  goals_source: 'ai' | 'manual' | string | null;
  goal_weight_kg: number | null;
  coach_name: string | null;
  training_days: string[] | null;
  scheduled_workouts: Record<string, any> | null;
  weekly_training_days: number | null;
  weight_unit: 'kg' | 'lbs' | null;
  onboarding_complete: boolean;
  has_seen_coach_intro: boolean;
  has_seen_welcome: boolean;
  has_viewed_plan: boolean;
  is_subscribed: boolean;
  subscription_ended_at?: string | null;
  // Persona & motivation for niche targeting
  coach_personality: string | null;
  primary_motivation: string | null;
  // Streak freeze
  streak_freezes: number;
  last_freeze_week: number | null;
  streak_freeze_used_at: string | null;
  // Proactive coach messaging — unread badge counter
  unread_coach_messages: number;
}

export interface FoodEntry {
  id: string;
  user_id: string;
  date: string;
  food_name: string;
  calories: number;
  protein: number | null;
  carbs: number | null;
  fats: number | null;
  meal_type: string;
  serving_size: number | null;
  serving_unit: string | null;
  meal_session_id?: string | null;
  created_at: string;
}

export interface Workout {
  id: string;
  user_id: string;
  date: string;
  name: string;
  duration: number | null;
  exercises: any;
  notes: string | null;
  created_at: string;
  type?: string | null;
  division?: string | null;
}

export interface MoodLog {
  id: string;
  user_id: string;
  date: string;
  mood_rating: number;
  sleep_hours: number | null;
  stress_level: number | null;
  notes: string | null;
  created_at: string;
}

// Local helper aliases — all keys live in src/lib/queryKeys.ts
const KEY_PROFILE = queryKeys.profile[0];
const KEY_FOOD_ENTRIES = queryKeys.foodEntries[0];
const KEY_WORKOUTS = queryKeys.workouts[0];
const KEY_MOOD_LOGS = queryKeys.moodLogs[0];

// Local storage keys for offline caching
const CACHE_KEYS = {
  profile: (userId: string) => `cached_profile_${userId}`,
  workouts: (userId: string) => `cached_workouts_${userId}`,
};

function getCachedProfile(userId: string): UserProfile | null {
  try {
    const cached = localStorage.getItem(CACHE_KEYS.profile(userId));
    if (cached) return JSON.parse(cached);
  } catch (error) {
    console.error('Error reading cached profile:', error);
  }
  return null;
}

function setCachedProfile(userId: string, profile: UserProfile): void {
  try {
    localStorage.setItem(CACHE_KEYS.profile(userId), JSON.stringify(profile));
  } catch (error) {
    console.error('Error caching profile:', error);
  }
}

// Re-export calculateLevel for backwards compatibility
export { calculateLevel } from '@/lib/gamification';

export function useProfile() {
  const { user } = useAuth();
  
  const query = useQuery({
    queryKey: [KEY_PROFILE, user?.id],
    queryFn: async () => {
      if (!user) return null;
      
      // Try cache first to render instantly without empty flash
      const cachedFirst = getCachedProfile(user.id);
      if (cachedFirst) {
      }
      
      // Try to fetch from network when online
      if (navigator.onLine) {
        try {
          const { data, error } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', user.id)
            .maybeSingle();
          
          if (error) throw error;
          
          if (data) {
            // Cache for offline use
            setCachedProfile(user.id, data as UserProfile);
          }
          
          return (data as UserProfile | null) ?? cachedFirst;
        } catch (error) {
          console.error('[Profile] Network fetch failed, falling back to cache:', error);
          if (cachedFirst) return cachedFirst;
        }
      }
      
      // Offline or fetch failed: use cached data
      if (cachedFirst) return cachedFirst;
      
      return null;
    },
    enabled: !!user,
    // Subscription status lives on the profile — keep it fresh so paid users
    // never see stale "Go Premium" UI after a purchase or restore.
    staleTime: 10 * 1000,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    placeholderData: () => {
      // Use cached profile as instant placeholder so UI never sees `undefined`
      if (!user) return null;
      return getCachedProfile(user.id) ?? undefined;
    },
  });
  
  return query;
}

export function useFoodEntries() {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: [KEY_FOOD_ENTRIES, user?.id],
    queryFn: async () => {
      if (!user) return [];
      const { data, error } = await supabase
        .from('food_entries')
        .select('*')
        .eq('user_id', user.id)
        .gte('date', format(subDays(new Date(), 30), 'yyyy-MM-dd'))
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data as FoodEntry[];
    },
    enabled: !!user,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

export function useWorkouts() {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: [KEY_WORKOUTS, user?.id],
    queryFn: async () => {
      if (!user) return [];
      
      // Try to fetch from network when online
      if (navigator.onLine) {
        try {
          const { data, error } = await supabase
            .from('workouts')
            .select('*')
            .eq('user_id', user.id)
            .gte('date', format(subDays(new Date(), 30), 'yyyy-MM-dd'))
            .order('created_at', { ascending: false });
          
          if (error) throw error;
          
          const workouts = data as Workout[];
          
          // Cache for offline use
          try {
            localStorage.setItem(CACHE_KEYS.workouts(user.id), JSON.stringify(workouts));
          } catch (e) {
            console.error('Error caching workouts:', e);
          }
          
          return workouts;
        } catch (error) {
          console.error('Error fetching workouts:', error);
          // Fall through to cached data
        }
      }
      
      // Offline or fetch failed: use cached data
      try {
        const cached = localStorage.getItem(CACHE_KEYS.workouts(user.id));
        if (cached) {
          return JSON.parse(cached) as Workout[];
        }
      } catch (e) {
        console.error('Error reading cached workouts:', e);
      }
      
      return [];
    },
    enabled: !!user,
    staleTime: 2 * 60 * 1000,
  });
}

export function useMoodLogs() {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: [KEY_MOOD_LOGS, user?.id],
    queryFn: async () => {
      if (!user) return [];
      const { data, error } = await supabase
        .from('mood_logs')
        .select('*')
        .eq('user_id', user.id)
        .gte('date', format(subDays(new Date(), 30), 'yyyy-MM-dd'))
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data as MoodLog[];
    },
    enabled: !!user,
    staleTime: 2 * 60 * 1000,
  });
}

export function useAddFoodEntry() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (entry: Omit<FoodEntry, 'id' | 'user_id' | 'created_at'>) => {
      if (!user) throw new Error('Not authenticated');
      
      // Use centralized session helper
      await ensureSession();
      
      const { data, error } = await supabase
        .from('food_entries')
        .insert({ ...entry, user_id: user.id })
        .select()
        .single();
      
      if (error) {
        console.error('[useAddFoodEntry] Insert error:', error);
        throw error;
      }
      return data;
    },
    onMutate: async (newEntry) => {
      await queryClient.cancelQueries({ queryKey: [KEY_FOOD_ENTRIES] });
      const previousEntries = queryClient.getQueryData([KEY_FOOD_ENTRIES, user?.id]);
      
      // Optimistic update
      queryClient.setQueryData([KEY_FOOD_ENTRIES, user?.id], (old: FoodEntry[] = []) => [
        { ...newEntry, id: 'temp-id', user_id: user?.id || '', created_at: new Date().toISOString() } as FoodEntry,
        ...old,
      ]);
      
      return { previousEntries };
    },
    onError: (err: any, _, context) => {
      queryClient.setQueryData([KEY_FOOD_ENTRIES, user?.id], context?.previousEntries);
      const message = err?.message?.includes('row-level security') 
        ? 'Unable to save - please sign in again'
        : 'Failed to add food';
      toast.error(message);
      haptics.error();
    },
    onSuccess: async () => {
      haptics.lightTap();
      await addXP(10, user?.id, queryClient);
      // Server-side recompute so calories/protein tasks move the health score.
      triggerScoreRecompute(user?.id, queryClient);
      // First-ever food log: one of the two backup push-permission moments.
      // Counted server-side (head count) so it can't be fooled by a fresh
      // install or an empty cache; emitPushPrompt itself is once-per-user.
      if (user?.id && !hasPushPromptFired('first_food_log', user.id)) {
        try {
          const { count } = await supabase
            .from('food_entries')
            .select('id', { count: 'exact', head: true })
            .eq('user_id', user.id);
          if (count === 1) emitPushPrompt('first_food_log', user.id);
        } catch {
          /* non-fatal — never block a food log on a prompt */
        }
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [KEY_FOOD_ENTRIES] });
    },
  });
}

export function useAddWorkout() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (workout: Omit<Workout, 'id' | 'user_id' | 'created_at'>) => {
      if (!user) throw new Error('Not authenticated');
      
      await ensureSession();
      
      const { data, error } = await supabase
        .from('workouts')
        .insert({ ...workout, user_id: user.id })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onMutate: async (newWorkout) => {
      await queryClient.cancelQueries({ queryKey: [KEY_WORKOUTS] });
      const previousWorkouts = queryClient.getQueryData([KEY_WORKOUTS, user?.id]);
      
      queryClient.setQueryData([KEY_WORKOUTS, user?.id], (old: Workout[] = []) => [
        { ...newWorkout, id: 'temp-id', user_id: user?.id || '', created_at: new Date().toISOString() } as Workout,
        ...old,
      ]);
      
      return { previousWorkouts };
    },
    onError: (err, _, context) => {
      queryClient.setQueryData([KEY_WORKOUTS, user?.id], context?.previousWorkouts);
      toast.error('Failed to add workout');
    },
    onSuccess: async () => {
      await addXP(50, user?.id, queryClient);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [KEY_WORKOUTS] });
    },
  });
}


export function useDeleteFoodEntry() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      if (!user) throw new Error('Not authenticated');
      await ensureSession();
      const { error } = await supabase
        .from('food_entries')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id);
      if (error) throw error;
    },
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: [KEY_FOOD_ENTRIES] });
      const previousEntries = queryClient.getQueryData([KEY_FOOD_ENTRIES, user?.id]);
      
      queryClient.setQueryData([KEY_FOOD_ENTRIES, user?.id], (old: FoodEntry[] = []) =>
        old.filter((e) => e.id !== id)
      );
      
      return { previousEntries };
    },
    onError: (err, _, context) => {
      queryClient.setQueryData([KEY_FOOD_ENTRIES, user?.id], context?.previousEntries);
      toast.error('Failed to delete entry');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [KEY_FOOD_ENTRIES] });
    },
  });
}

export function useUpdateFoodEntry() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<FoodEntry> }) => {
      if (!user) throw new Error('Not authenticated');
      await ensureSession();
      const { data, error } = await supabase
        .from('food_entries')
        .update(updates)
        .eq('id', id)
        .eq('user_id', user.id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onMutate: async ({ id, updates }) => {
      await queryClient.cancelQueries({ queryKey: [KEY_FOOD_ENTRIES] });
      const previousEntries = queryClient.getQueryData([KEY_FOOD_ENTRIES, user?.id]);
      
      queryClient.setQueryData([KEY_FOOD_ENTRIES, user?.id], (old: FoodEntry[] = []) =>
        old.map((e) => e.id === id ? { ...e, ...updates } : e)
      );
      
      return { previousEntries };
    },
    onError: (err, _, context) => {
      queryClient.setQueryData([KEY_FOOD_ENTRIES, user?.id], context?.previousEntries);
      toast.error('Failed to update entry');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [KEY_FOOD_ENTRIES] });
    },
  });
}

export function useDeleteWorkout() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      if (!user) throw new Error('Not authenticated');
      await ensureSession();
      const { error } = await supabase
        .from('workouts')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id);
      if (error) throw error;
    },
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: [KEY_WORKOUTS] });
      const previousWorkouts = queryClient.getQueryData([KEY_WORKOUTS, user?.id]);
      
      queryClient.setQueryData([KEY_WORKOUTS, user?.id], (old: Workout[] = []) =>
        old.filter((w) => w.id !== id)
      );
      
      return { previousWorkouts };
    },
    onError: (err, _, context) => {
      queryClient.setQueryData([KEY_WORKOUTS, user?.id], context?.previousWorkouts);
      toast.error('Failed to delete workout');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [KEY_WORKOUTS] });
    },
  });
}


export function useUpdateProfile() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (updates: Partial<UserProfile>) => {
      if (!user) throw new Error('Not authenticated');
      await ensureSession();
      const { error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', user.id);
      if (error) throw error;
    },
    onMutate: async (updates) => {
      await queryClient.cancelQueries({ queryKey: [KEY_PROFILE] });
      const previousProfile = queryClient.getQueryData([KEY_PROFILE, user?.id]);
      
      queryClient.setQueryData([KEY_PROFILE, user?.id], (old: UserProfile | null) =>
        old ? { ...old, ...updates } : null
      );
      
      return { previousProfile };
    },
    onError: (err, _, context) => {
      queryClient.setQueryData([KEY_PROFILE, user?.id], context?.previousProfile);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [KEY_PROFILE] });
    },
  });
}

// Helper to add XP and update streak
export async function addXP(amount: number, userId: string | undefined, queryClient: any) {
  if (!userId) return;

  // Always read the latest profile from the DB to avoid stale-cache streak misses
  const { data: freshProfile } = await supabase
    .from('profiles')
    .select('total_xp, current_streak, longest_streak, last_active_date')
    .eq('id', userId)
    .maybeSingle();

  const profile = freshProfile ?? (queryClient.getQueryData([KEY_PROFILE, userId]) as UserProfile | null);
  if (!profile) return;

  const today = format(new Date(), 'yyyy-MM-dd');
  const yesterday = format(subDays(new Date(), 1), 'yyyy-MM-dd');

  const newXP = (profile.total_xp || 0) + amount;
  const newLevel = calculateLevel(newXP);
  const prevLevel = calculateLevel(profile.total_xp || 0);

  // Calculate streak
  let newStreak = profile.current_streak || 0;
  const lastActive = profile.last_active_date;

  if (lastActive !== today) {
    if (lastActive === yesterday) {
      newStreak = newStreak + 1;
    } else if (!lastActive || isBefore(parseISO(lastActive), parseISO(yesterday))) {
      newStreak = 1;
    } else {
      // Fallback: ensure streak is at least 1 on a fresh active day
      newStreak = Math.max(newStreak, 1);
    }
  } else if (newStreak < 1) {
    // Safety net: `last_active_date` can read as today without any real
    // activity behind it (legacy reconcile rows). Real activity today must
    // never leave the streak at 0.
    newStreak = 1;
  }


  const newLongest = Math.max(newStreak, profile.longest_streak || 0);

  await supabase
    .from('profiles')
    .update({
      total_xp: newXP,
      level: newLevel,
      current_streak: newStreak,
      longest_streak: newLongest,
      last_active_date: today
    })
    .eq('id', userId);

  // Optimistic update
  queryClient.setQueryData([KEY_PROFILE, userId], (old: UserProfile | null) =>
    old ? {
      ...old,
      total_xp: newXP,
      level: newLevel,
      current_streak: newStreak,
      longest_streak: newLongest,
      last_active_date: today
    } : null
  );

  // Reward the progress ticks at the source: a streak increment or a level-up.
  if (newStreak > (profile.current_streak || 0) || newLevel > prevLevel) {
    haptics.success();
  }

  // Streak STARTED — the 0 -> 1 transition only. Every later streak day has
  // a previous streak >= 1, so this never re-fires on day 2, 3, ...
  // emitPushPrompt is additionally once-per-user, so even a streak that
  // breaks and restarts won't ask twice.
  if ((profile.current_streak || 0) === 0 && newStreak === 1) {
    emitPushPrompt('streak_started', userId);
  }

  // Streak milestones and level-ups update silently in profile data;
  // the UI surfaces them via streak bubble + XP bar (no popups during normal use).
}

// Hook for pull-to-refresh
export function useRefreshData() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  
  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: [KEY_PROFILE, user?.id] }),
      queryClient.invalidateQueries({ queryKey: [KEY_FOOD_ENTRIES, user?.id] }),
      queryClient.invalidateQueries({ queryKey: [KEY_WORKOUTS, user?.id] }),
      queryClient.invalidateQueries({ queryKey: [KEY_MOOD_LOGS, user?.id] }),
    ]);
  };
  
  return refresh;
}

// Total workout count (lifetime, not 30-day window)
export function useTotalWorkoutCount() {
  const { user } = useAuth();
  return useQuery({
    queryKey: [...queryKeys.workoutsTotalCount, user?.id],
    queryFn: async () => {
      if (!user) return 0;
      const { count, error } = await supabase
        .from('workouts')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id);
      if (error) throw error;
      return count || 0;
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
  });
}

// Total HYROX race count (lifetime)
export function useTotalHyroxCount() {
  const { user } = useAuth();
  return useQuery({
    queryKey: [...queryKeys.hyroxTotalCount, user?.id],
    queryFn: async () => {
      if (!user) return 0;
      const { count, error } = await (supabase
        .from('workouts')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id) as any)
        .eq('type', 'hyrox');
      if (error) throw error;
      return count || 0;
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
  });
}

